import React from 'react';
import { Accordion, AccordionSummary, AccordionDetails, Box, Typography } from '@mui/material';
import { ExpandMore, Lock as LockIcon } from '@mui/icons-material';

interface ProvenanceRecord {
  recordId?: string;
  publicRendering?: string;
  internalRendering?: string;
  isPublic?: boolean;
}

interface ProvenanceSectionProps {
  provenanceData: Array<Record<string, unknown>>;
  isPublicRoute: boolean;
}

const subheadingSx = {
  fontSize: '0.85rem',
  letterSpacing: '0.1em',
  fontWeight: 700,
  color: 'text.secondary',
  textTransform: 'uppercase' as const,
  mt: 1,
  mb: 0.5,
};

export const ProvenanceSection: React.FC<ProvenanceSectionProps> = ({
  provenanceData,
  isPublicRoute,
}) => {
  const records = provenanceData as ProvenanceRecord[];
  const publicRecords = records.filter((r) => r.publicRendering && r.isPublic !== false);
  const internalRecords = isPublicRoute
    ? []
    : records.filter((r) => r.isPublic === false || (r.internalRendering && !r.publicRendering));

  if (publicRecords.length === 0 && internalRecords.length === 0) return null;

  return (
    <Accordion data-testid="provenance-section">
      <AccordionSummary expandIcon={<ExpandMore />}>
        <Typography variant="h3">Provenance</Typography>
      </AccordionSummary>
      <AccordionDetails>
        {publicRecords.length > 0 && (
          <>
            <Typography sx={subheadingSx}>Public Provenance</Typography>
            <Box sx={{ pl: 1 }}>
              {publicRecords.map((record, idx) => (
                <Typography key={record.recordId ?? idx} variant="body1" sx={{ mb: 1 }}>
                  {record.publicRendering}
                </Typography>
              ))}
            </Box>
          </>
        )}

        {!isPublicRoute && internalRecords.length > 0 && (
          <>
            <Typography sx={subheadingSx}>Always Private Provenance</Typography>
            <Box sx={{ pl: 1 }}>
              {internalRecords.map((record, idx) => {
                const publicText = record.publicRendering || '';
                const internalText = record.internalRendering || '';
                return (
                  <Box key={record.recordId ?? idx} sx={{ mb: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <Box
                        data-testid="provenance-private-indicator"
                        sx={{ display: 'inline-flex', alignItems: 'center', flexShrink: 0 }}
                      >
                        <LockIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
                      </Box>
                      <Typography variant="body1">
                        {publicText || internalText}
                      </Typography>
                    </Box>
                    {publicText && internalText && (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5, pl: 2.5 }}>
                        <Box
                          data-testid="provenance-private-indicator"
                          sx={{ display: 'inline-flex', alignItems: 'center', flexShrink: 0 }}
                        >
                          <LockIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
                        </Box>
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{ fontStyle: 'italic' }}
                        >
                          {internalText}
                        </Typography>
                      </Box>
                    )}
                  </Box>
                );
              })}
            </Box>
          </>
        )}
      </AccordionDetails>
    </Accordion>
  );
};
